const getChildren = group => Array
    .from(document.querySelectorAll(`[data-checkbox-group^="${group}."]`))
;

const getSelectableChildren = group => getChildren(group).filter(child => !child.disabled);

const groupIsDeterminate = checkboxes => {
    let accumulator = null;

    return checkboxes.every(checkbox => {
        if (checkbox.indeterminate) {
            return false;
        }

        if (accumulator === null) {
            accumulator = checkbox.checked;
            return true;
        }

        return accumulator === checkbox.checked;
    });
};

export const checkParentDetermination = group => {
    if (!group.includes('.')) {
        return;
    }

    const parentGroup = group.substring(0, group.lastIndexOf('.'));
    const parent = document.querySelector(`[data-checkbox-group="${parentGroup}"]`);

    if (!parent) {
        checkParentDetermination(parentGroup);
        return;
    }

    const children = getSelectableChildren(parentGroup);

    parent.indeterminate = children.length > 0 && !groupIsDeterminate(children);
    parent.checked = children.length > 0 && children.every(c => c.checked);

    checkParentDetermination(parentGroup);
};

const lastChecked = new Map();

const getCheckedChildren = group => getChildren(group).filter(c => c.checked);

const updateCheckAllLabel = trigger => {
    const scope = trigger.closest('form').querySelector(trigger.dataset.checkAll);
    const checkboxes = [...scope.querySelectorAll('input[type="checkbox"]:not(:disabled)')];

    trigger.textContent = checkboxes.length > 0 && checkboxes.every(checkbox => checkbox.checked)
        ? trigger.dataset.deselectAllLabel
        : trigger.dataset.selectAllLabel
    ;
};

const updateCheckAllLabels = root => {
    if (!root) {
        return;
    }

    const triggers = [...root.querySelectorAll('[data-check-all]')];

    if (root.matches?.('[data-check-all]')) {
        triggers.push(root);
    }

    triggers.forEach(updateCheckAllLabel);
};

export const updateContextElements = group => {
    const rootGroup = group.includes('.') ? group.substring(0, group.indexOf('.')) : group;
    const contextElements = document.querySelectorAll(`[data-checkbox-group-context="${rootGroup}"]`);

    if (contextElements.length === 0) {
        return;
    }

    const checked = getCheckedChildren(rootGroup);

    contextElements.forEach(element => {
        const countElement = element.querySelector('.count');

        if (countElement) {
            countElement.textContent = checked.length;
        }

        element.classList.toggle('visible', checked.length > 0);
    });
};

export const refreshContextElements = root => {
    const checkboxes = [...root.querySelectorAll('[data-checkbox-group]')];

    if (root.matches('[data-checkbox-group]')) {
        checkboxes.push(root);
    }

    new Set(checkboxes.map(checkbox => checkbox.dataset.checkboxGroup.split('.')[0]))
        .forEach(updateContextElements);
};

export const getContextIds = group => {
    const checked = getCheckedChildren(group);

    return checked.map(checkbox => {
        const key = checkbox.dataset.checkboxGroup;
        return key.substring(group.length + 1);
    });
};

export const appendContextIdsToRequest = event => {
    const context = event.detail.elt.closest('[data-checkbox-group-context]');

    if (!context) {
        return;
    }

    event.detail.parameters['ids'] = getContextIds(context.dataset.checkboxGroupContext).join(',');
};

export const checkAllOnDropdownOpen = container => {
    if (container.classList.contains('active')) {
        return;
    }

    const group = container.dataset.checkAllOnOpen;

    if (getCheckedChildren(group).length > 0) {
        return;
    }

    getSelectableChildren(group).forEach(checkbox => {
        checkbox.checked = true;
        checkbox.indeterminate = false;
    });

    const parent = document.querySelector(`[data-checkbox-group="${group}"]`);

    if (parent) {
        parent.checked = true;
        parent.indeterminate = false;
    }

    updateContextElements(group);
};

export const handleCheckboxGroupClick = (checkbox, event) => {
    const group = checkbox.dataset.checkboxGroup;
    const children = getSelectableChildren(group);

    if (children.length > 0) {
        children.forEach(child => {
            child.checked = checkbox.checked;
            child.indeterminate = false;
        });
    }

    if (group.includes('.')) {
        const parentGroup = group.substring(0, group.lastIndexOf('.'));
        const siblings = getChildren(parentGroup);
        const currentIndex = siblings.indexOf(checkbox);
        const previousIndex = lastChecked.get(parentGroup) ?? null;

        lastChecked.set(parentGroup, currentIndex);

        if (previousIndex !== null && event.shiftKey && previousIndex !== currentIndex) {
            const checkState = siblings[previousIndex].checked;
            const start = previousIndex > currentIndex ? currentIndex : previousIndex + 1;
            const end = previousIndex < currentIndex ? currentIndex : previousIndex - 1;

            for (let i = start; i <= end; i++) {
                if (siblings[i].disabled) {
                    continue;
                }

                siblings[i].checked = checkState;
            }
        }

        checkParentDetermination(group);
    }

    updateCheckAllLabels(checkbox.closest('form'));
};

export const initializeCheckboxGroups = root => {
    root
        .querySelectorAll('input[type="checkbox"][data-checkbox-group]:checked')
        .forEach(checkbox => checkParentDetermination(checkbox.dataset.checkboxGroup))
    ;

    updateCheckAllLabels(root);
};

export const toggleAllInScope = trigger => {
    const scope = trigger.closest('form').querySelector(trigger.dataset.checkAll);
    const checkboxes = [...scope.querySelectorAll('input[type="checkbox"]:not(:disabled)')];
    const checked = !checkboxes.every(checkbox => checkbox.checked);

    checkboxes.forEach(checkbox => {
        checkbox.checked = checked;
        checkbox.indeterminate = false;
    });

    new Set(checkboxes.map(checkbox => checkbox.dataset.checkboxGroup?.split('.')[0]).filter(Boolean))
        .forEach(updateContextElements);

    updateCheckAllLabel(trigger);
    scope.dispatchEvent(new Event('change', {bubbles: true}));
};
